import React, { useEffect, useState } from "react";
import { getOverdueSummary } from "../services/overdueSummaryService";
import { tData } from "../i18n";
import { usePanelTheme } from "../context/themeContext";

function formatMoney(value) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("de-DE");
}

export default function OverdueWarningPanel({ language = "de" }) {
  const t = tData?.[language] || tData?.de || {};
  const { theme } = usePanelTheme();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    count: 0,
    totalOutstanding: 0,
    rows: [],
  });

  const loadData = async (force = false) => {
    setLoading(true);
    try {
      const data = await getOverdueSummary({ force, maxItems: 6 });
      setSummary(data || { count: 0, totalOutstanding: 0, rows: [] });
    } catch (error) {
      console.error("Error loading overdue warning panel:", error);
      setSummary({ count: 0, totalOutstanding: 0, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(false);
  }, []);

  const warnWrap =
    theme === "dark"
      ? "rounded-[24px] border border-amber-800/70 bg-amber-950/40 p-5 shadow-sm"
      : "rounded-[24px] border border-amber-300 bg-amber-50 p-5 shadow-sm";

  const warnTextPrimary =
    theme === "dark" ? "text-amber-200" : "text-amber-900";

  const warnTextSecondary =
    theme === "dark" ? "text-amber-300/85" : "text-amber-700";

  const warnAccent =
    theme === "dark" ? "text-amber-300" : "text-amber-700";

  const warnButton =
    theme === "dark"
      ? "rounded-2xl border border-amber-700/60 px-4 py-2 text-sm font-semibold text-amber-200 transition-colors hover:bg-amber-900/40"
      : "rounded-2xl border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100";

  const rowClass =
    theme === "dark"
      ? "rounded-2xl border border-amber-800/40 bg-amber-950/20 p-4 transition-colors hover:bg-amber-900/20"
      : "rounded-2xl border border-amber-200 bg-white p-4 transition-colors hover:bg-amber-50";

  if (loading) {
    return (
      <div className={warnWrap}>
        <div className={`text-sm font-semibold ${warnAccent}`}>
          {t.loadingOverdueWarnings || "Loading overdue warnings..."}
        </div>
      </div>
    );
  }

  if (!summary.count) {
    return (
      <div
        className={
          theme === "dark"
            ? "rounded-[24px] border border-emerald-800/60 bg-emerald-950/30 p-5 shadow-sm"
            : "rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 shadow-sm"
        }
      >
        <div
          className={
            theme === "dark"
              ? "text-sm font-semibold text-emerald-300"
              : "text-sm font-semibold text-emerald-800"
          }
        >
          {t.noOverdueInvoices || "No overdue invoices right now."}
        </div>
      </div>
    );
  }

  return (
    <div className={warnWrap}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className={`text-xs font-bold uppercase tracking-[0.18em] ${warnAccent}`}>
            {t.attention || "Attention"}
          </div>

          <div className={`mt-2 text-2xl font-black ${warnTextPrimary}`}>
            {summary.count}{" "}
            {t.overdueInvoices || `overdue invoice${summary.count === 1 ? "" : "s"}`}
          </div>

          <div className={`mt-1 text-sm ${warnTextSecondary}`}>
            {t.openTotal || "Open total"}: {formatMoney(summary.totalOutstanding)}
          </div>
        </div>

        <button type="button" onClick={() => loadData(true)} className={warnButton}>
          {t.refresh || "Refresh"}
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {summary.rows.map((row) => (
          <div key={row.id} className={rowClass}>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className={`font-semibold ${warnTextPrimary}`}>
                  {row.invoiceNumber || "—"}
                </div>

                <div className={`mt-1 text-sm ${warnTextSecondary}`}>
                  {row.clientName || "—"}
                </div>

                <div className={`mt-1 text-xs font-semibold ${warnAccent}`}>
                  {t.due || "Due"}: {formatDate(row.dueDate)}
                </div>
              </div>

              <div className="text-right">
                <div className={`font-black ${warnTextPrimary}`}>
                  {formatMoney(row.outstanding)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
